import {ArrayElement} from 'webapp-lib/typings';

import communicationBackend from '@lexian/api/communication-api';
import {Artifact} from '@lexian/api/log-view-api';
import searchApi from '@lexian/api/search-api';
import {TaskStateUpdateParams} from '@lexian/api/tasks-management-async-backend';
import {PossibleFeatureFlagsType} from '_/hooks/useFeatureFlagCheck';

export type {LoggedInUserInformation} from '@lexian/api/user-api';

export type ModuleInfo = {
  id: string;
  text: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  module: string;
  requiredFeatureFlag?: PossibleFeatureFlagsType;
  requiresOneOfPermissions?: GlobalPermissionsType | GlobalPermissionsType[];
  modulePath: string;
};

export type HiddenModuleInfo = {
  module: string;
  id: string;
};

export enum State {
  Loading = 'Loading',
  Success = 'Success',
  Error = 'Error',
}

export type Color = string;

// Electron adds a `path` attribute to the `File` type
// {@link https://github.com/electron/electron/blob/main/docs/api/file-object.md}
export type ElectronFile = File & {path?: string};

export enum Positioning {
  TopLeftAlignDown,
  BottomLeftAlignDown,
  BottomRightAlignDown,
  TopRightAlignDown,
  TopRightOutsetDown,
  TopLeftOutsetDown,
}

// This function cannot be converted to an arrow function because
// the never typings don't work properly anymore when in an arrow function
export function assertUnreachable(
  _: never,
  message = "Didn't expect to get here"
): never {
  throw new Error(message);
}

export enum PaginationDirection {
  Forward = 'FORWARD',
  Backward = 'BACKWARD',
}

export type PaginationDirectionType = `${PaginationDirection}`;

export enum PaginationMode {
  Browse = 'BROWSE',
  Refresh = 'REFRESH',
  Navigate = 'NAVIGATE_TO_ITEM',
  Jump = 'JUMP_TO_POSITION',
}

export type PaginationModeType = `${PaginationMode}`;

export const defaultPaginationArguments = {
  cursor: null,
  direction: PaginationDirection.Forward,
  mode: PaginationMode.Browse,
} as {
  cursor: null;
  direction: PaginationDirection.Forward;
  mode: PaginationMode.Browse;
};

export type SortEndpointArguments = {
  sortFields: string[];
  sortOrders: string[];
};

export type PaginationArguments =
  | {
      mode?: PaginationMode.Browse;
      cursor?: string | null;
      direction?: PaginationDirection;
      key?: undefined;
      sortFields?: undefined;
    }
  | {
      mode?: PaginationMode.Refresh;
      cursor?: string | null;
      direction?: PaginationDirection;
      key?: undefined;
      sortFields?: undefined;
    }
  | {
      mode?: PaginationMode.Navigate;
      cursor?: undefined;
      direction?: PaginationDirection;
      key: string[];
      sortFields?: undefined;
    }
  | {
      mode?: PaginationMode.Jump;
      cursor?: undefined;
      direction?: PaginationDirection;
      key?: undefined;
      sortFields?: string[];
    };

export type PaginationArgumentsWithoutCursor = Omit<
  PaginationArguments,
  'cursor'
>;

export type PaginatedEndpointArguments = {
  paginationCursor?: string;
  paginationDirection: 'FORWARD' | 'BACKWARD';
  paginationKeyComponents?: string[];
  paginationMode:
    | 'BROWSE'
    | 'REFRESH'
    | 'NAVIGATE_TO_ITEM'
    | 'JUMP_TO_POSITION';
  paginationSortFieldValues?: string[];
};

export type PaginationResponse<Type> = {
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  items: Type[];
};

export type Loadable<T> = {
  data?: T;
  state: State;
  abort?: () => void;
};

export type DataLoadable<T> = {
  data: T;
  state: State;
};

export type RequiredDataLoadable<T> = Optional<Required<Loadable<T>>, 'abort'>;

export type PaginatedLoadable<T> = Loadable<T> & {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
};

export type PageItem<T> = {
  cursor: string;
  item: T;
};

/** Utility to get the resolved value of a promise. */
export type Awaited<T> = T extends PromiseLike<infer U> ? Awaited<U> : T;

export const isNotNullOrUndefined = <T>(
  item: T | null | undefined
): item is T => item != null;

export type ArtifactEntityType = Artifact['entityType'];

export const ArtifactEntityTypes: Record<
  ArtifactEntityType,
  ArtifactEntityType
> = {
  EMAIL: 'EMAIL',
  DOCUMENT: 'DOCUMENT',
  NOTE: 'NOTE',
  TASK: 'TASK',
};

export enum DocumentType {
  WORD_DOCUMENT = 'WORD_DOCUMENT',
  EXCEL_DOCUMENT = 'EXCEL_DOCUMENT',
  POWERPOINT_DOCUMENT = 'POWERPOINT_DOCUMENT',
  EMAIL_DOCUMENT = 'EMAIL_DOCUMENT',
  PDF_DOCUMENT = 'PDF_DOCUMENT',
  IMAGE_DOCUMENT = 'IMAGE_DOCUMENT',
  TEXT_DOCUMENT = 'TEXT_DOCUMENT',
  RTF_DOCUMENT = 'RTF_DOCUMENT',
  ZIP_DOCUMENT = 'ZIP_DOCUMENT',
  UNKNOWN_DOCUMENT = 'UNKNOWN_DOCUMENT',
}

export type DocumentTypeUnion = `${DocumentType}`;

export type ArtifactType = Artifact['type'];

export type ArtifactTypeUnion = `${ArtifactType}`;

export const ArtifactTypes: Record<ArtifactType, ArtifactType> = {
  EMAIL: 'EMAIL',
  NOTE: 'NOTE',
  EMAIL_DOCUMENT: 'EMAIL_DOCUMENT',
  EXCEL_DOCUMENT: 'EXCEL_DOCUMENT',
  IMAGE_DOCUMENT: 'IMAGE_DOCUMENT',
  PDF_DOCUMENT: 'PDF_DOCUMENT',
  POWERPOINT_DOCUMENT: 'POWERPOINT_DOCUMENT',
  TEXT_DOCUMENT: 'TEXT_DOCUMENT',
  RTF_DOCUMENT: 'RTF_DOCUMENT',
  WORD_DOCUMENT: 'WORD_DOCUMENT',
  ZIP_DOCUMENT: 'ZIP_DOCUMENT',
  UNKNOWN_DOCUMENT: 'UNKNOWN_DOCUMENT',
  TASK: 'TASK',
};

export enum TransactionFileType {
  XML = 'XML',
  Unknown = 'UNKNOWN',
}

export type TemplateFileType =
  | 'EMAIL'
  | 'EXCEL'
  | 'POWERPOINT'
  | 'WORD'
  | 'ZIP';

export const EXTENSION_FROM_TEMPLATE_FILE_TYPE: Record<
  TemplateFileType,
  string
> = {
  WORD: 'docx',
  EXCEL: 'xlsx',
  POWERPOINT: 'pptx',
  EMAIL: 'eml',
  ZIP: 'zip',
};

export const TEMPLATE_FILE_TYPE_TO_DOCUMENT_TYPE: Record<
  TemplateFileType,
  DocumentType
> = {
  EMAIL: DocumentType.EMAIL_DOCUMENT,
  EXCEL: DocumentType.EXCEL_DOCUMENT,
  POWERPOINT: DocumentType.POWERPOINT_DOCUMENT,
  WORD: DocumentType.WORD_DOCUMENT,
  ZIP: DocumentType.UNKNOWN_DOCUMENT,
};

export type FileWithMeta = File & {
  createdAt?: string;
  modifiedAt?: string;
};

type ProgressEvent = {
  direction: 'download' | 'upload';
  loaded: number;
  percent?: number | undefined;
  total?: number | undefined;
};

export type QueryParameters = {[param: string]: any};

export type CommonRequestOptions = {
  $queryParameters?: QueryParameters;
  $domain?: string;
  $path?: string | ((path: string) => string);
  $progressCallback?: (arg0: ProgressEvent) => void;
  $retries?: number;
  $timeout?: number;
  $deadline?: number;
  $signal?: AbortSignal;
};

/**
 * Global module agnostic money type
 */
export type Money = {amount: string};

/**
 * Optional makes a single field of a type optional
 */
export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type ValueOf<T> = T[keyof T];

/**
 * Global permissions as they are saved on the backend, used to hide elements for users
 */
export const GlobalPermissions = {
  Invoicing: {
    ManageDiscounts: 'ManageDiscounts',
    ManageAccountingExport: 'ManageAccountingExport',
    ManageClientMoney: 'ManageClientMoney',
    ManagePayments: 'ManagePayments',
    ManageTransactions: 'ManageTransactions',
    ManageInvoices: 'ManageInvoices',
    CancelInvoice: 'CancelInvoice',
  },
  Settings: {
    Admin: 'Admin',
    ManageRoles: 'ManageRoles',
    ManageSpecialityFields: 'ManageSpecialityFields',
    ManageUsers: 'ManageUsers',
    ManageTags: 'ManageTags',
    ManageTemplates: 'ManageTemplates',
    ManageAccountingExportSettings: 'ManageAccountingExportSettings',
    ManageGroupHourlyRateSettings: 'ManageGroupHourlyRateSettings',
    ManageCasusTemplates: 'ManageCasusTemplates',
    ManageOfficeLocations: 'ManageOfficeLocations',
    ManageCasusArchivingLocations: 'ManageCasusArchivingLocations',
    ManageBankAccounts: 'ManageBankAccounts',
    ManageFinancialInstitutions: 'ManageFinancialInstitutions',
    ManageVatNumbers: 'ManageVatNumbers',
    ManageHourlyRates: 'ManageHourlyRates',
    ManageEmailAccounts: 'ManageEmailAccounts',
    ManageEmailClients: 'ManageEmailClients',
    ManageDocumentLocks: 'ManageDocumentLocks',
  },
  Casus: {
    CreateCasus: 'CreateCasus',
    ManageCasusDeletion: 'ManageCasusDeletion',
    ManageCasusProtection: 'ManageCasusProtection',
  },
  Justitia: {
    ManageJustitia: 'ManageJustitia',
  },
  Obligations: {
    ManageObligations: 'ManageObligations',
  },
} as const;

export type GlobalPermissionsType =
  | ValueOf<typeof GlobalPermissions.Invoicing>
  | ValueOf<typeof GlobalPermissions.Settings>
  | ValueOf<typeof GlobalPermissions.Casus>
  | ValueOf<typeof GlobalPermissions.Justitia>
  | ValueOf<typeof GlobalPermissions.Obligations>;

export type NoteConfidentiality = 'INTERNAL' | 'EXTERNAL';

export type ContactType = NonNullable<
  ArrayElement<
    Parameters<(typeof searchApi)['search']>[0]['parameters']['contactTypes']
  >
>;

export type TaskState = TaskStateUpdateParams['state'];

export type EmailDirectionFilterType = NonNullable<
  Parameters<
    (typeof communicationBackend)['getEmails']
  >[0]['parameters']['direction']
>;

export type EmailInternalFilterType = NonNullable<
  Parameters<
    (typeof communicationBackend)['getEmails']
  >[0]['parameters']['internal']
>;

export type EmailPriorityType = 'HIGH' | 'NORMAL' | 'LOW';
